import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import stripeService from '../services/stripeService';
import storeService from '../services/storeService';
import { useAuth } from './AuthContext';

const StoreContext = createContext(null);

const FIG_LOGO = 'https://figk-12.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png';

export const StoreProvider = ({ children }) => {
  const { user, isSuperAdmin, userStoreId } = useAuth();
  const [stripeConfig, setStripeConfig] = useState(null);
  const [currentStore, setCurrentStoreState] = useState(null);
  const [allStores, setAllStores] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadStripeConfig = useCallback(async (storeId) => {
    try {
      const configs = await stripeService.getPublicConfigs();
      // Try store-specific config first, then default
      let config = storeId ? configs.find(c => c.StoreId === storeId && c.isActive) : null;
      if (!config) {
        config = await stripeService.getDefaultPublicKey();
      }
      setStripeConfig(config);
      console.log('[Store] Stripe config loaded:', config?.Name);
    } catch (error) {
      console.error('[Store] Failed to load Stripe config:', error);
    }
  }, []);

  const loadStores = useCallback(async () => {
    try {
      const stores = await storeService.getActive();
      setAllStores(stores);
      return stores;
    } catch (error) {
      console.error('[Store] Failed to load stores:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const stores = await loadStores();

      // If logged-in store owner, auto-select their store
      if (userStoreId) {
        const myStore = stores.find(s => s.apiid === userStoreId);
        if (myStore) setCurrentStoreState(myStore);
      }

      await loadStripeConfig(userStoreId);
      setLoading(false);
    };
    init();
  }, [userStoreId, loadStores, loadStripeConfig]);

  const setCurrentStore = async (store) => {
    setCurrentStoreState(store);
    if (store) {
      await loadStripeConfig(store.apiid);
    } else {
      await loadStripeConfig(null);
    }
  };

  const loadStoreBySlug = async (slug) => {
    try {
      const store = await storeService.getBySlug(slug);
      if (store) {
        setCurrentStoreState(store);
        await loadStripeConfig(store.apiid);
      }
      return store;
    } catch (error) {
      console.error('[Store] Failed to load store by slug:', error);
      return null;
    }
  };

  const refreshStripeConfig = () => loadStripeConfig(currentStore?.apiid);

  const storeLogo = currentStore?.LogoUrl || FIG_LOGO;
  const storeName = currentStore?.Name || 'FIG Marketplace';

  const taxPercent = Number(currentStore?.TaxPercent) || 0;
  const shippingFee = Number(currentStore?.ShippingFee) || 0;
  const freeShippingThreshold = Number(currentStore?.FreeShippingThreshold) || 0;

  return (
    <StoreContext.Provider value={{
      stripeConfig, loading, refreshStripeConfig,
      currentStore, allStores, setCurrentStore, loadStoreBySlug, loadStores,
      storeLogo, storeName,
      taxPercent, shippingFee, freeShippingThreshold,
      isSuperAdmin: isSuperAdmin?.() || false,
    }}>
      {children}
    </StoreContext.Provider>
  );
};

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used within StoreProvider');
  return ctx;
};
